import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import {
  faCheck,
  faChevronDown,
  faCircleQuestion,
  faCircleX,
  faQuestionCircle,
  faFileUpload,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleHalf } from '@fortawesome/pro-solid-svg-icons';
import { SmallBodyText, Tooltip } from 'ui-toolkit';
import { Listbox, Transition } from '@headlessui/react';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';

export interface IDropdownProps {
  title: string;
  options: readonly string[];
  option: string;
  color?: string;
  icon?: React.ReactNode;
  style?: 'default' | 'progress' | 'complete' | 'error' | 'document';
  tooltipText?: string;
  isLoading?: boolean;
  onSelect: (option: any) => void;
}

const colorSchemes = new Map(
  [
    { key: 'default', value: { style: 'text-inherit bg-gray-50 border-gray-300', icon: faQuestionCircle } },
    { key: 'progress', value: { style: 'text-white bg-yellow-500 border-yellow-700', icon: faCircleHalf } },
    { key: 'complete', value: { style: 'text-white bg-green-500 border-green-700', icon: faCheck } },
    { key: 'error', value: { style: 'text-white bg-red-500 border-red-700', icon: faCircleX } },
    { key: 'document', value: { style: 'text-inherit bg-gray-50 border-gray-300', icon: faFileUpload } },
  ].map((obj) => {
    return [obj.key, obj.value];
  })
);

export const Dropdown: React.FC<IDropdownProps> = ({
  options,
  option,
  title,
  tooltipText,
  onSelect,
  isLoading,
  style = 'default',
}) => {
  return (
    <div>
      <div className={'flex flex-row items-center gap-1'}>
        <p className="text-sm font-medium">{title}</p>
        {tooltipText && (
          <Tooltip
            placement={'bottom'}
            tooltipContent={
              <>
                <SmallBodyText intent="italic">{tooltipText}</SmallBodyText>
              </>
            }
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </Tooltip>
        )}
      </div>
      <Listbox value={option} onChange={onSelect}>
        <Listbox.Button className="inline-flex rounded-md shadow-sm" style={{ minWidth: '180px' }}>
          <div
            className={`inline-flex flex-1 items-center gap-1.5 rounded-l-md border-y border-l py-2 px-3 ${colorSchemes.get(
              style
            )?.style}`}
          >
            <FontAwesomeIcon className="-ml-0.5" icon={colorSchemes.get(style)?.icon || faCircleQuestion} />
            {!isLoading && <p className="max-w-sm overflow-hidden text-ellipsis text-sm font-medium">{option}</p>}
            {isLoading && <SpinnerTW />}
          </div>
          <div
            className={`rounded-l-0 inline-flex items-center self-stretch rounded-r-md border p-2 ${colorSchemes.get(
              style
            )?.style}`}
            aria-labelledby="headlessui-listbox-label-1 headlessui-listbox-button-2"
          >
            <span
              className="nowrap absolute -m-px h-px w-px overflow-hidden whitespace-nowrap border-0 p-0"
              style={{
                clip: 'rect(0, 0, 0, 0)',
              }}
            >
              Yes
            </span>
            <FontAwesomeIcon icon={faChevronDown} />
          </div>
        </Listbox.Button>
        <Transition
          className="absolute z-10"
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Listbox.Options className="align-center flex flex-col rounded-md bg-white shadow-lg">
            {options.map((opt) => (
              <Listbox.Option
                key={opt}
                value={opt}
                className="align-start flex flex-col gap-2 p-4 ui-active:bg-green-50"
              >
                <span className={'max-w-sm overflow-hidden text-ellipsis'}>{opt}</span>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </Listbox>
    </div>
  );
};
